
class IngredientApp {
  // Normalize the metaobject `visible` field (boolean, string, or list)
  // into a simple yes/no. Treats true / "true" / "1" / "yes" as visible.
  static isVisible(value) {
    const truthy = (v) => v === true || v === "true" || v === "1" || v === "yes";
    if (Array.isArray(value)) return value.some(truthy);
    return truthy(value);
  }

  constructor(config) {
    // Configuration / DOM selectors
    this.$grid = document.querySelector(config.gridSelector);
    this.$searchInput = document.querySelector(config.searchSelector);
    this.$searchBtn = document.querySelector(config.searchBtnSelector);
    this.$sortSelect = document.querySelector(config.sortSelector);
    this.$loadMoreBtn = document.querySelector(config.loadMoreSelector);
    this.$noResultsMessage = document.querySelector(config.noResultsSelector);
    this.$countBadge = document.querySelector("#ingredientsCount");

    // Data & state
    const rawData = config.data || [];

    // --- DEBUG: inspect what the Liquid metaobject loop produced ---
    console.log("[Ingredients] total entries from metaobject:", rawData.length);
    console.log(
      "[Ingredients] raw `visible` values:",
      rawData.map((item) => ({ title: item.title, visible: item.visible }))
    );

    // Keep only entries explicitly marked visible.
    // Handles boolean true, string "true", or a list/array containing either.
    this.allData = rawData.filter((item) => IngredientApp.isVisible(item.visible));

    console.log(
      `[Ingredients] visible after filter: ${this.allData.length} of ${rawData.length}`
    );
    if (this.allData.length === 0 && rawData.length > 0) {
      console.warn(
        "[Ingredients] Every entry was filtered out. Check the `visible` values logged above — none matched true/\"true\"."
      );
    }
    // --- END DEBUG ---
    this.filteredData = []; // data after applying search & sort
    this.currentPage = 1;
    this.itemsPerPage = config.itemsPerPage || 6;
    this.sortOrder = "title-asc"; // default
    this.searchQuery = "";
  }

  init() {
    // Bind event listeners
    this.$searchBtn.addEventListener("click", () => this.onSearch());
    this.$searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.onSearch();
      }
    });

    this.$sortSelect.addEventListener("change", () => this.onSortChange());
    this.$loadMoreBtn.addEventListener("click", () => this.onLoadMore());

    // Initial filtering/sorting/render
    this.applyFiltersAndSort();
    this.renderCurrentPage();
  }

  onSearch() {
    const query = this.$searchInput.value.trim().toLowerCase();
    this.searchQuery = query;
    this.currentPage = 1;
    this.applyFiltersAndSort();
    this.renderCurrentPage();
  }

  onSortChange() {
    this.sortOrder = this.$sortSelect.value;
    this.currentPage = 1;
    this.applyFiltersAndSort();
    this.renderCurrentPage();
  }

  onLoadMore() {
    this.currentPage++;
    this.renderCurrentPage();
  }

  applyFiltersAndSort() {
    // 1) Filter by searchQuery (case‐insensitive substring match on title)
    if (this.searchQuery.length > 0) {
      this.filteredData = this.allData.filter((item) =>
        item.title.toLowerCase().includes(this.searchQuery)
      );
    } else {
      // no search, copy entire dataset
      this.filteredData = [...this.allData];
    }

    // 2) Sort the filteredData in place
    const compareFn = (a, b) => {
      if (this.sortOrder === "title-asc") {
        return a.title.localeCompare(b.title);
      } else if (this.sortOrder === "title-desc") {
        return b.title.localeCompare(a.title);
      }
      return 0;
    };
    this.filteredData.sort(compareFn);

    if (this.$countBadge) {
      this.$countBadge.textContent = `(${this.filteredData.length})`;
    }
  }

  getPaginatedData() {
    const endIdx = this.currentPage * this.itemsPerPage;
    return this.filteredData.slice(0, endIdx);
  }

  renderCurrentPage() {
    // Clear grid
    this.$grid.innerHTML = "";

    // If no data at all
    if (this.filteredData.length === 0) {
      this.$noResultsMessage.hidden = false;
      this.$loadMoreBtn.style.display = "none";
      return;
    } else {
      this.$noResultsMessage.hidden = true;
    }

    // Get items to render (pagination)
    const itemsToRender = this.getPaginatedData();

    // Create & append cards
    for (const item of itemsToRender) {
      const card = this.createCard(item);
      this.$grid.appendChild(card);
    }

    // If we've shown every matching item, hide “Load More”; otherwise show it
    if (itemsToRender.length >= this.filteredData.length) {
      this.$loadMoreBtn.style.display = "none";
    } else {
      this.$loadMoreBtn.style.display = "inline-block";
    }
  }

  createCard(item) {
    // 1) Create an <a> instead of a <div>, so the whole card is clickable
    const cardLink = document.createElement("div");
    cardLink.classList.add("card");
    // cardLink.href = item.url || "#";

    // 2) Icon (image URL)
    if (item.iconURL) {
      const iconWrapper = document.createElement("div");
      iconWrapper.classList.add("icon-wrapper");
      iconWrapper.classList.add("maskulin-icon-wrapper");
      const img = document.createElement("img");
      img.src = item.iconURL;
      img.alt = `${item.title} icon`;
      iconWrapper.appendChild(img);
      cardLink.appendChild(iconWrapper);
    }

    // 3) Title
    const contentWrapper = document.createElement("div");
    contentWrapper.classList.add("content-wrapper");
    const titleEl = document.createElement("h2");
    titleEl.classList.add("title");
    titleEl.textContent = item.title;
    contentWrapper.appendChild(titleEl);

    // 4) Description
    const descEl = document.createElement("p");
    descEl.classList.add("description");
    descEl.textContent = item.description;
    contentWrapper.appendChild(descEl);

    // 5) “Learn More” inner link
    const learnMore = document.createElement("a");
    learnMore.href = item.url || "#";
    learnMore.classList.add("learn-more");
    learnMore.title = "Learn More";
    learnMore.textContent = "Learn More";
    contentWrapper.appendChild(learnMore);

    // 5.5) “Learn More” inner link
    const learnMoreSpan = document.createElement("span");
    learnMoreSpan.classList.add("mkl-learn-more-svg");
    learnMoreSpan.setAttribute("aria-hidden", "true");
    learnMoreSpan.innerHTML = `
  <span class="mkl-line-wrap">
    <span class="mkl-line mkl-line-1"></span>
    <span class="mkl-line mkl-line-2"></span>
  </span>
  <span class="mkl-arrow">
    <span class="mkl-arrow-inner">
      <svg viewBox="0 0 10 12" focusable="false">
        <polyline points="1.5,1 8,6 1.5,11" />
      </svg>
    </span>
  </span>
`;
    learnMore.prepend(learnMoreSpan);

    // 6)
    cardLink.appendChild(contentWrapper);

    return cardLink;
  }
}

// Parse a <script type="application/json"> block into an array (safely).
function parseIngredientsJSON(el) {
  if (!el) return [];
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.error("[Ingredients] Failed to parse JSON data:", e);
    return [];
  }
}

// All entries are rendered inline as JSON by the section (using offset loops to
// bypass Shopify's 50-per-loop metaobject cap). We just parse and de-dupe here.
function loadAllIngredients() {
  const el = document.getElementById("ingredients-data");
  let data = parseIngredientsJSON(el);

  // Safety net: drop any accidental duplicates by URL.
  const seen = new Set();
  data = data.filter((item) => {
    if (!item || !item.url || seen.has(item.url)) return false;
    seen.add(item.url);
    return true;
  });

  console.log(`[Ingredients] loaded ${data.length} unique entries`);
  return data;
}

document.addEventListener("DOMContentLoaded", () => {
  const data = loadAllIngredients();
  const app = new IngredientApp({
    data,
    gridSelector: "#ingredientsGrid",
    searchSelector: "#searchInput",
    searchBtnSelector: "#searchBtn",
    sortSelector: "#sortSelect",
    loadMoreSelector: "#loadMoreBtn",
    noResultsSelector: "#noResultsMessage",
    itemsPerPage: 12,
  });
  app.init();
});
